import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn';
import styles from './Menu.module.css';

export function Menu({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn(styles.menu, className)} role="menu" {...rest}>{children}</div>;
}

export interface MenuItemProps extends HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  destructive?: boolean;
  kbd?: string;
  leftIcon?: ReactNode;
}

/**
 * One entry in a menu. Pressed as a button is: a click, or Enter or Space once
 * it has focus, so an action that lives only in a menu is never out of the
 * keyboard's reach (Codex review round 29). A disabled entry does nothing,
 * whichever way it is pressed.
 */
export function MenuItem({ disabled, destructive, kbd, leftIcon, className, children, onClick, onKeyDown, ...rest }: MenuItemProps) {
  return (
    <div
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={cn(styles.item, destructive && styles.destructive, disabled && styles.disabled, className)}
      {...rest}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(event) => {
        if (disabled) return;
        onKeyDown?.(event);
        // The item's own keys only: a control inside it answers its own
        // (Codex review round 30).
        if (event.defaultPrevented || event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Every press is the item's, a held key's repeats too, so Space never
        // scrolls the page; only the first presses it.
        event.preventDefault();
        if (!event.repeat) event.currentTarget.click();
      }}
    >
      {leftIcon && <span className={styles.icon}>{leftIcon}</span>}
      <span className={styles.label}>{children}</span>
      {kbd && <span className={styles.kbd}>{kbd}</span>}
    </div>
  );
}

export function MenuDivider() {
  return <div className={styles.divider} aria-hidden />;
}
